/**
 * utils/bordereauGenerator.ts
 *
 * Generates a "Bordereau de pièces communiquées" in two formats:
 * - .doc (HTML wrapped in a Word-compatible blob) via generateBordereauDocBlob()
 * - .pdf (generated with pdf-lib) via generateBordereauPdfBytes()
 *
 * Both formats produce identical content:
 * - Title: "BORDEREAU DE PIECES COMMUNIQUEES" (centered, bold)
 * - Horizontal separator line below the title
 * - Two-column list: "Pièce X" bold on the left, label on the right
 *   with generous vertical spacing
 *
 * Updated Apr 2026: replaced bordered table layout with clean borderless list
 * matching the reference screenshot. "Pièce N°X" simplified to "Pièce X".
 * Updated Apr 2026 (dossier bundle refactor):
 * - Replaced downloadBordereauDoc/downloadBordereauPdf with
 *   generateBordereauDocBlob/generateBordereauPdfBytes.
 * Updated Apr 2026: DOC - replaced invisible HTML table with Word mso tab-stop
 * approach: each row is a <p> with mso-style-tab-stops and a tab character
 * (mso-tab-count:1) to align the label column at 4cm.
 * Updated Apr 2026: PDF rendering delegated to shared renderBordereauPages
 * helper (bordereauPdfRenderer.ts) for dynamic pagination and maxWidth wrapping.
 */

import { PDFDocument, StandardFonts } from 'pdf-lib';
import type { DossierPiece } from '../types/dossier';
import { renderBordereauPages } from './bordereauPdfRenderer';

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildBordereauHtml(pieces: DossierPiece[]): string {
  const rows = pieces
    .map(
      (p, i) =>
        `<p style="font-size:11pt;margin:0 0 18pt 0;mso-style-tab-stops:left 4.0cm;">` +
        `<b>Pi&#232;ce ${i + 1}</b>` +
        `<span style="mso-tab-count:1;">&#9;</span>` +
        `${escapeHtml(p.label)}` +
        `</p>`
    )
    .join('\n');

  return `
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:w="urn:schemas-microsoft-com:office:word"
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
  <meta charset="utf-8" />
  <meta name=ProgId content=Word.Document>
  <title>Bordereau de pieces communiquees</title>
  <style>
    @page {
      margin: 2.5cm;
    }
    body {
      font-family: "Calibri", "Arial", sans-serif;
      font-size: 11pt;
      color: #111;
      margin: 0;
    }
    h1 {
      font-family: "Calibri", "Arial", sans-serif;
      font-size: 14pt;
      font-weight: bold;
      text-align: center;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-bottom: 6pt;
    }
    hr {
      border: none;
      border-top: 1pt solid #999;
      margin: 0 0 24pt 0;
    }
  </style>
</head>
<body>
  <h1>Bordereau de pieces communiquees</h1>
  <hr />
  ${rows}
</body>
</html>`;
}

export function generateBordereauDocBlob(pieces: DossierPiece[]): Blob {
  const html = buildBordereauHtml(pieces);
  return new Blob([html], { type: 'application/msword' });
}

export async function generateBordereauPdfBytes(pieces: DossierPiece[]): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.create();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  renderBordereauPages({ pdfDoc, font, fontBold, pieces });

  return pdfDoc.save();
}
